/**
 * Token Refresh Service
 * 
 * Handles automatic refresh of access tokens using refresh tokens
 */

const REFRESH_TOKEN_KEY = 'refreshToken';
const ACCESS_TOKEN_KEY = 'accessToken';

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: number;
    email: string;
    createdAt: Date;
  };
}

/**
 * Store refresh token in localStorage
 */
export function storeRefreshToken(refreshToken: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Get refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Clear refresh token from localStorage
 */
export function clearRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Refresh the access token using the refresh token
 * Returns new tokens or null if refresh fails
 */
export async function refreshAccessToken(): Promise<RefreshTokenResponse | null> {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    console.warn('⚠️ tokenRefreshService: No refresh token found');
    return null;
  }

  try {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('⚠️ tokenRefreshService: Refresh token expired or invalid');
        // Clear invalid refresh token
        clearRefreshToken();
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem('user');
        // Dispatch event to notify AuthContext
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
      }
      return null;
    }

    const data: RefreshTokenResponse = await response.json();
    
    // Store new tokens
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    if (data.refreshToken) {
      storeRefreshToken(data.refreshToken);
    }
    
    // Update user data if provided
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    console.log('✅ tokenRefreshService: Successfully refreshed access token');
    return data;
  } catch (error) {
    console.error('❌ tokenRefreshService: Failed to refresh token:', error);
    return null;
  }
}

/**
 * Check if we should attempt to refresh the token
 * Returns true if token is expired or about to expire soon (within 1 hour)
 */
export function shouldRefreshToken(accessToken: string | null): boolean {
  if (!accessToken) {
    return false;
  }

  try {
    // Decode JWT to check expiration
    const parts = accessToken.split('.');
    if (parts.length !== 3) {
      return false;
    }

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = exp - now;
    
    // Refresh if expired or expiring within 1 hour
    return timeUntilExpiry <= 60 * 60 * 1000;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return false;
  }
}

